/**
 * Auth Client
 *
 * Frontend authentication state management.
 * Checks session on page load, provides login/logout helpers,
 * and exposes user state for feature gating.
 */

let _currentUser = null;
let _initialized = false;

/**
 * Initialize auth state by checking the session cookie with the server.
 * Call once on page load.
 */
export async function initAuth() {
  if (_initialized) return _currentUser;
  _initialized = true;

  try {
    const headers = {};
    const qaTier = sessionStorage.getItem('qa-tier');
    if (qaTier) headers['X-QA-Tier'] = qaTier;

    const response = await fetch('/api/auth/me', {
      credentials: 'include',
      headers,
    });

    if (response.ok) {
      const data = await response.json();
      _currentUser = data.user;
    } else {
      _currentUser = null;
    }
  } catch (err) {
    console.error('Auth check failed:', err);
    _currentUser = null;
  }

  return _currentUser;
}

/**
 * Get the current authenticated user (or null).
 */
export function getUser() {
  return _currentUser;
}

/**
 * Check if the current user is authenticated.
 */
export function isAuthenticated() {
  return _currentUser !== null;
}

/**
 * Check if the current user has any paid subscription (designer or ultra).
 */
export function isPro() {
  return ['designer', 'ultra'].includes(_currentUser?.tier);
}

/**
 * Check if the current user has the Designer tier.
 */
export function isDesigner() {
  return _currentUser?.tier === 'designer';
}

/**
 * Check if the current user has the Ultra tier.
 */
export function isUltra() {
  return _currentUser?.tier === 'ultra';
}

/**
 * Check if the current user is a QA tester.
 */
export function isQA() {
  return _currentUser?.realTier === 'qa';
}

/**
 * Get the current QA simulated tier (stored in sessionStorage).
 */
export function getQATier() {
  if (!isQA()) return null;
  return sessionStorage.getItem('qa-tier') || null;
}

/**
 * Set QA tier simulation. Reloads the page to apply.
 */
export function setQATier(tier) {
  if (!isQA()) return;
  if (tier) {
    sessionStorage.setItem('qa-tier', tier);
  } else {
    sessionStorage.removeItem('qa-tier');
  }
  window.location.reload();
}

/**
 * Get the user's subdomain.
 */
export function getUserSubdomain() {
  return _currentUser?.subdomain || null;
}

/**
 * Redirect to Google OAuth.
 */
export function loginWithGoogle() {
  window.location.href = '/api/auth/google';
}

/**
 * Redirect to Facebook OAuth.
 */
export function loginWithFacebook() {
  window.location.href = '/api/auth/facebook';
}

/**
 * Register with email and password.
 * @param {string} email
 * @param {string} password
 * @param {string} displayName
 * @param {string} turnstileToken - Cloudflare Turnstile token
 * @returns {Promise<{ok: boolean, error?: string, message?: string}>}
 */
export async function registerWithEmail(email, password, displayName, turnstileToken) {
  try {
    const response = await fetch('/api/auth/register', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, displayName, turnstileToken }),
    });

    return await response.json();
  } catch (err) {
    return { ok: false, error: 'Network error' };
  }
}

/**
 * Login with email and password.
 * @param {string} email
 * @param {string} password
 * @param {string} turnstileToken
 * @returns {Promise<{ok: boolean, user?: object, error?: string}>}
 */
export async function loginWithEmail(email, password, turnstileToken) {
  try {
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, turnstileToken }),
    });

    const data = await response.json();
    if (data.ok && data.user) {
      _currentUser = data.user;
    }
    return data;
  } catch (err) {
    return { ok: false, error: 'Network error' };
  }
}

/**
 * Logout — clear session.
 */
export async function logout() {
  try {
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
    });
  } catch (err) {
    // ignore
  }
  _currentUser = null;
  window.location.reload();
}

/**
 * Request a password reset email.
 */
export async function forgotPassword(email, turnstileToken) {
  try {
    const response = await fetch('/api/auth/forgot-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, turnstileToken }),
    });
    return await response.json();
  } catch (err) {
    return { ok: false, error: 'Network error' };
  }
}

/**
 * Reset password with a token.
 */
export async function resetPassword(token, password) {
  try {
    const response = await fetch('/api/auth/reset-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, password }),
    });
    return await response.json();
  } catch (err) {
    return { ok: false, error: 'Network error' };
  }
}

/**
 * Start Stripe checkout for subscription upgrade.
 * @param {'monthly' | 'annual'} plan
 */
export async function startCheckout(plan = 'monthly') {
  try {
    const response = await fetch('/api/subscription/checkout', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan }),
    });

    const data = await response.json();
    if (data.url) {
      window.location.href = data.url;
    }
    return data;
  } catch (err) {
    return { error: 'Network error' };
  }
}

/**
 * Call the AI designer endpoint.
 * @param {string} prompt
 * @param {File} [file]
 * @returns {Promise<{ok: boolean, design?: object, error?: string}>}
 */
export async function aiAssist(prompt, file) {
  try {
    const formData = new FormData();
    formData.append('prompt', prompt);
    if (file) formData.append('file', file);

    const response = await fetch('/api/ai-assist', {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    return await response.json();
  } catch (err) {
    return { ok: false, error: 'Network error' };
  }
}
